/** 公司管理员: 邀请成员 (生成邀请链接 / 复制 / 邮件发送 / 作废) */
import { useState } from "react";
import { Ban, Check, Copy, Link2, Mail, Plus } from "lucide-react";
import { errorDetail } from "@/lib/api-client";
import {
  Alert, Badge, Button, Field, Input, PageHeader, Select, Spinner,
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow, toast, type BadgeProps,
} from "@/components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FormDialog } from "../components/FormDialog";
import { useCreateInvite, useEmailInvite, useInvites, useRevokeInvite } from "./hooks";
import type { CreateInvitePayload, Invite } from "./api";

const STATUS: Record<Invite["status"], { label: string; variant: BadgeProps["variant"] }> = {
  active: { label: "有效", variant: "success" },
  revoked: { label: "已作废", variant: "neutral" },
  expired: { label: "已过期", variant: "warning" },
  exhausted: { label: "已用完", variant: "neutral" },
};

function fmtTime(s: string | null) {
  return s ? s.replace("T", " ").slice(0, 16) : "永不过期";
}

export function InvitesPage() {
  const { data, isLoading, error } = useInvites();
  const createMut = useCreateInvite();
  const revokeMut = useRevokeInvite();

  const [showCreate, setShowCreate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<Invite | null>(null);
  const [emailFor, setEmailFor] = useState<Invite | null>(null);
  const [copied, setCopied] = useState<number | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const copy = async (inv: Invite) => {
    try {
      await navigator.clipboard.writeText(inv.invite_url);
      setCopied(inv.id);
      toast.success("已复制邀请链接");
      setTimeout(() => setCopied((c) => (c === inv.id ? null : c)), 1500);
    } catch {
      window.prompt("复制邀请链接", inv.invite_url);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader
        title="邀请成员"
        description="把邀请链接发给同事，对方用手机号注册后即加入本公司。"
        actions={<Button icon={<Plus />} onClick={() => setShowCreate(true)}>生成邀请链接</Button>}
      />

      {(error || actionErr) && (
        <Alert onClose={actionErr ? () => setActionErr(null) : undefined}>
          {actionErr ?? errorDetail(error, "加载失败")}
        </Alert>
      )}

      {showCreate && (
        <CreateInviteForm
          pending={createMut.isPending}
          onClose={() => setShowCreate(false)}
          onSubmit={async (p) => {
            const r = await createMut.mutateAsync(p);
            setShowCreate(false);
            void copy(r.data);
          }}
        />
      )}

      {emailFor && <EmailInviteForm invite={emailFor} onClose={() => setEmailFor(null)} />}

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>邀请码</TableHead>
            <TableHead className="min-w-[6rem]">备注</TableHead>
            <TableHead numeric>已用 / 上限</TableHead>
            <TableHead>过期时间</TableHead>
            <TableHead className="w-20">状态</TableHead>
            <TableHead className="w-28 text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <tr>
              <td colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2"><Spinner /> 加载中…</span>
              </td>
            </tr>
          )}
          {data && data.data.length === 0 && (
            <TableEmpty
              colSpan={6}
              icon={Link2}
              title="还没有邀请链接"
              action={<Button size="sm" icon={<Plus />} onClick={() => setShowCreate(true)}>生成邀请链接</Button>}
            />
          )}
          {data?.data.map((inv) => {
            const st = STATUS[inv.status] ?? STATUS.revoked;
            const active = inv.status === "active";
            return (
              <TableRow key={inv.id}>
                <TableCell className="whitespace-nowrap font-mono text-xs">{inv.code}</TableCell>
                <TableCell>{inv.note || <span className="text-muted-foreground">-</span>}</TableCell>
                <TableCell numeric>{inv.used} / {inv.max_uses ?? "不限"}</TableCell>
                <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{fmtTime(inv.expires_at)}</TableCell>
                <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                <TableCell className="text-right">
                  {active && (
                    <div className="inline-flex items-center gap-0.5">
                      <Button variant="ghost" size="icon-sm" onClick={() => copy(inv)} aria-label="复制链接" title="复制链接"
                        className="text-muted-foreground hover:text-primary">
                        {copied === inv.id ? <Check className="text-success" /> : <Copy />}
                      </Button>
                      <Button variant="ghost" size="icon-sm" onClick={() => setEmailFor(inv)} aria-label="邮件发送" title="邮件发送"
                        className="text-muted-foreground hover:text-primary">
                        <Mail />
                      </Button>
                      <Button variant="ghost" size="icon-sm" onClick={() => setConfirmRevoke(inv)} aria-label="作废" title="作废"
                        className="text-muted-foreground hover:bg-danger-soft hover:text-danger-strong">
                        <Ban />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={!!confirmRevoke}
        title={`作废邀请码 "${confirmRevoke?.code ?? ""}"`}
        message="作废后该链接无法再用于注册，已加入的成员不受影响。"
        destructive
        confirmText="作废"
        onCancel={() => setConfirmRevoke(null)}
        onConfirm={() => {
          if (confirmRevoke) {
            setActionErr(null);
            const code = confirmRevoke.code;
            revokeMut.mutate(confirmRevoke.id, {
              onSuccess: () => toast.success(`已作废邀请码 ${code}`),
              onError: (e) => setActionErr(errorDetail(e, "作废失败")),
            });
          }
          setConfirmRevoke(null);
        }}
      />
    </div>
  );
}

function CreateInviteForm({
  pending, onClose, onSubmit,
}: {
  pending: boolean;
  onClose: () => void;
  onSubmit: (p: CreateInvitePayload) => Promise<void>;
}) {
  const [maxUses, setMaxUses] = useState("");
  const [hours, setHours] = useState("168");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const handle = async () => {
    setErr(null);
    const mu = maxUses.trim() ? Number(maxUses) : null;
    if (mu !== null && (!Number.isInteger(mu) || mu < 1)) {
      setErr("使用次数需为正整数，留空为不限");
      return;
    }
    try {
      await onSubmit({ max_uses: mu, expires_in_hours: Number(hours), note: note.trim() });
    } catch (e) {
      setErr(errorDetail(e, "生成失败"));
    }
  };

  return (
    <FormDialog title="生成邀请链接" description="生成后会自动复制到剪贴板" pending={pending}
      submitText="生成并复制" pendingText="生成中…" onClose={onClose} onSubmit={handle} err={err}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="可用次数" hint="留空不限">
          <Input type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} className="tabular-nums" />
        </Field>
        <Field label="有效期">
          <Select value={hours} onChange={(e) => setHours(e.target.value)}>
            <option value="24">1 天</option>
            <option value="168">7 天</option>
            <option value="720">30 天</option>
            <option value="0">永不过期</option>
          </Select>
        </Field>
      </div>
      <Field label="备注" hint="可空">
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </FormDialog>
  );
}

function EmailInviteForm({ invite, onClose }: { invite: Invite; onClose: () => void }) {
  const mut = useEmailInvite();
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handle = async () => {
    setErr(null);
    if (!email.trim()) {
      setErr("请填写邮箱");
      return;
    }
    try {
      await mut.mutateAsync({ code: invite.code, email: email.trim() });
      setDone(true);
    } catch (e) {
      setErr(errorDetail(e, "发送失败"));
    }
  };

  return (
    <FormDialog
      title="邮件发送邀请"
      description={<>邀请码 <span className="font-mono">{invite.code}</span></>}
      pending={mut.isPending}
      submitText="发送"
      pendingText="发送中…"
      cancelText="关闭"
      onClose={onClose}
      onSubmit={handle}
      err={err}
    >
      <Field label="对方邮箱">
        <Input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setDone(false); }}
          placeholder="name@example.com" />
      </Field>
      {done && <Alert variant="success">已发送到 {email}</Alert>}
    </FormDialog>
  );
}
